import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Star } from 'lucide-react';
import { BrandRatingSummary } from '../../api/payment/brand-deal.api';
import { RATING_LIMITS, ratingDueLabel, validateRating } from '../../utils/brand-rating';

/**
 * Ô chấm điểm 1–5 sao + nhận xét (M5, Task 3) — bắt buộc nhận xét khi chấm từ 3 sao trở xuống (spec
 * §2.2, global-constraints). Dùng chung creator/doanh nghiệp (`brand-rating-pending.tsx`) và chi tiết
 * deal/nhiệm vụ (`brand-deals.page.tsx`/`brand-campaign-detail.tsx`/bàn soạn bài M3).
 */

export interface BrandRatingFormProps {
  onSubmit: (stars: number, comment: string) => Promise<void> | void;
  busy?: boolean;
  submitLabel?: string;
}

const STAR_VALUES = [1, 2, 3, 4, 5];

const BrandRatingForm: React.FC<BrandRatingFormProps> = ({ onSubmit, busy, submitLabel = 'Gửi đánh giá' }) => {
  const [stars, setStars] = useState(0);
  const [hoverStars, setHoverStars] = useState(0);
  const [comment, setComment] = useState('');

  const commentRequired = stars > 0 && stars <= RATING_LIMITS.lowStarsThreshold;
  const displayStars = hoverStars || stars;

  const handleSubmit = async () => {
    const error = validateRating({ stars, comment });
    if (error) {
      toast.error(error);
      return;
    }
    await onSubmit(stars, comment.trim());
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1" role="radiogroup" aria-label="Số sao">
        {STAR_VALUES.map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={stars === n}
            aria-label={`${n} sao`}
            onClick={() => setStars(n)}
            onMouseEnter={() => setHoverStars(n)}
            onMouseLeave={() => setHoverStars(0)}
            disabled={!!busy}
            className="disabled:opacity-60"
          >
            <Star
              size={24}
              className={n <= displayStars ? 'fill-amber-400 text-amber-400' : 'ui-muted'}
            />
          </button>
        ))}
      </div>

      {stars > 0 && (
        <label className="block text-[13px]">
          <span className="ui-muted">
            Nhận xét {commentRequired ? '(bắt buộc, 10–500 ký tự)' : '(không bắt buộc, tối đa 500 ký tự)'}
          </span>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={RATING_LIMITS.commentMaxLength}
            rows={3}
            disabled={!!busy}
            className="mt-1 w-full resize-none rounded-md border ui-divider bg-transparent px-3 py-2 text-[14px] outline-none ui-text disabled:opacity-60"
            placeholder={
              commentRequired ? 'Hãy cho biết vì sao bạn đánh giá thấp…' : 'Vài dòng nhận xét (không bắt buộc)…'
            }
          />
          <span className="ui-muted float-right text-[12px]">
            {comment.length}/{RATING_LIMITS.commentMaxLength}
          </span>
        </label>
      )}

      <button
        type="button"
        disabled={!!busy || stars === 0}
        onClick={handleSubmit}
        className="clear-both w-fit rounded-lg bg-[#0057FF] px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60"
      >
        {busy ? 'Đang gửi…' : submitLabel}
      </button>
    </div>
  );
};

export default BrandRatingForm;

// ─────────── Ô chấm + đánh giá đã công bố trong chi tiết deal M1 / bàn soạn bài M3 ───────────
// Dùng chung creator/doanh nghiệp — `brand-deals.page.tsx` (M1), `brand-campaign-detail.tsx` (M1 phía
// doanh nghiệp), `brand-task-workspace.page.tsx` (M3 creator), `brand-task-review.tsx` (M3 doanh
// nghiệp, KHÔNG hiện ở admin — `myRating`/`theirRating`/`ratingDueAt` đều null ở GET phía admin).

const RatingStars: React.FC<{ stars: number }> = ({ stars }) => (
  <span className="text-amber-500" aria-label={`${stars} trên 5 sao`}>
    {'★'.repeat(stars)}
    <span className="ui-muted">{'★'.repeat(5 - stars)}</span>
  </span>
);

export interface BrandRatingBoxProps {
  /** null/undefined ⇒ deal/nhiệm vụ chưa mở chấm (chưa xong) hoặc là nhiệm vụ nhìn từ phía admin — ẩn
   * hẳn khối (không phải lỗi, chỉ chưa tới lúc). */
  ratingDueAt?: string | null;
  myRating?: BrandRatingSummary | null;
  theirRating?: BrandRatingSummary | null;
  /** Nhãn phía bên kia đã chấm — "Doanh nghiệp đánh giá bạn"/"Creator đánh giá bạn". */
  counterpartLabel: string;
  onSubmit: (stars: number, comment: string) => Promise<void> | void;
  busy?: boolean;
}

export const BrandRatingBox: React.FC<BrandRatingBoxProps> = ({
  ratingDueAt,
  myRating,
  theirRating,
  counterpartLabel,
  onSubmit,
  busy,
}) => {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  if (!ratingDueAt && !myRating && !theirRating) return null;

  const expired = !ratingDueAt || Date.parse(ratingDueAt) <= nowMs;

  return (
    <div className="ui-card flex flex-col gap-3 p-4">
      <h3 className="ui-heading text-[14px] font-bold">Đánh giá</h3>

      {myRating ? (
        <div className="text-[13px]">
          <div className="ui-text">
            Bạn đã đánh giá <RatingStars stars={myRating.stars} />
          </div>
          {myRating.comment && <p className="ui-muted mt-1 whitespace-pre-wrap">{myRating.comment}</p>}
        </div>
      ) : expired ? (
        <p className="ui-muted text-[13px]">Đã hết hạn đánh giá.</p>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="ui-muted text-[13px]">{ratingDueLabel(ratingDueAt, nowMs)}</p>
          <BrandRatingForm busy={busy} onSubmit={onSubmit} />
        </div>
      )}

      {theirRating && (
        <div className="border-t ui-divider pt-3 text-[13px]">
          <div className="ui-text">
            {counterpartLabel} <RatingStars stars={theirRating.stars} />
          </div>
          {theirRating.comment && <p className="ui-muted mt-1 whitespace-pre-wrap">{theirRating.comment}</p>}
        </div>
      )}
    </div>
  );
};
